import React from 'react';
import { Shield, Lock, CheckCircle2, Clock, Info, TrendingUp, AlertCircle } from 'lucide-react';
import { escrowData, formatPrice } from '../data/mockData';

export default function SmartEscrow() {
  const { totalAmount, disbursedAmount, lockedAmount, milestones, escrowNumber, paketName, provider } = escrowData;
  const progressPct = Math.round((disbursedAmount / totalAmount) * 100);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold dark:text-white text-[#1A202C] mb-1">Smart Escrow</h2>
        <p className="text-sm dark:text-gray-400 text-gray-500">Keamanan dana perjalanan Anda dijamin teknologi escrow berlisensi OJK</p>
      </div>

      {/* Main Summary Card */}
      <div data-testid="escrow-summary-card" className="rounded-2xl p-6 mb-6 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0] relative overflow-hidden">
        {/* Decorative glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#F5A623]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#00C9B1]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10">
          {/* Shield badge */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#F5A623]/10 flex items-center justify-center">
              <Shield size={24} className="text-[#F5A623]" />
            </div>
            <div>
              <p className="text-xs dark:text-gray-500 text-gray-400 uppercase tracking-wider font-semibold">Total Dana Diamankan</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs dark:text-gray-500 text-gray-400">No. Escrow:</span>
                <span className="text-xs font-mono text-[#00C9B1]">{escrowNumber}</span>
              </div>
            </div>
            <div className="ml-auto">
              <span className="flex items-center gap-1.5 text-xs font-bold bg-[#00C9B1]/10 text-[#00C9B1] px-3 py-1.5 rounded-full border border-[#00C9B1]/20">
                <div className="w-1.5 h-1.5 rounded-full bg-[#00C9B1] animate-pulse" />
                Aktif & Aman
              </span>
            </div>
          </div>

          {/* Big Number */}
          <div className="mb-6">
            <p className="text-4xl sm:text-5xl font-black text-[#F5A623] mb-1">
              {formatPrice(totalAmount)}
            </p>
            <p className="text-sm dark:text-gray-400 text-gray-500">
              <span className="font-medium dark:text-gray-300 text-gray-600">{paketName}</span>
              {' · '}{provider}
            </p>
          </div>

          {/* Split Summary */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="dark:bg-[#242430] bg-[#F7FAFC] rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={16} className="text-[#00C9B1]" />
                <span className="text-xs font-semibold text-[#00C9B1] uppercase tracking-wider">Dana Dicairkan</span>
              </div>
              <p className="text-xl font-black dark:text-white text-[#1A202C]">{formatPrice(disbursedAmount)}</p>
              <p className="text-xs dark:text-gray-500 text-gray-400 mt-1">20% dari total</p>
            </div>
            <div className="dark:bg-[#242430] bg-[#F7FAFC] rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={16} className="text-[#F5A623]" />
                <span className="text-xs font-semibold text-[#F5A623] uppercase tracking-wider">Dana Terkunci</span>
              </div>
              <p className="text-xl font-black dark:text-white text-[#1A202C]">{formatPrice(lockedAmount)}</p>
              <p className="text-xs dark:text-gray-500 text-gray-400 mt-1">80% dari total</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-xs dark:text-gray-500 text-gray-400 mb-2">
              <span>Progress Pencairan</span>
              <span className="font-semibold text-[#00C9B1]">{progressPct}%</span>
            </div>
            <div className="w-full h-3 dark:bg-[#2D3748] bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#00C9B1] to-[#F5A623] transition-all duration-700"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Security Info */}
      <div className="rounded-2xl p-4 mb-6 bg-[#00C9B1]/5 border border-[#00C9B1]/15 flex items-start gap-3">
        <Info size={16} className="text-[#00C9B1] flex-shrink-0 mt-0.5" />
        <p className="text-xs dark:text-gray-300 text-[#2D3748] leading-relaxed">
          Dana escrow Anda dikelola oleh <span className="font-semibold text-[#00C9B1]">PT. Escrow Indonesia (berlisensi OJK)</span>. 
          Pencairan hanya dilakukan setelah setiap milestone diverifikasi dan dikonfirmasi oleh tim UMROHLOKA. 
          Anda berhak mengajukan refund jika milestone tidak terpenuhi.
        </p>
      </div>

      {/* Milestones */}
      <div>
        <h3 className="font-bold dark:text-white text-[#1A202C] text-lg mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-[#00C9B1]" />
          Milestone Pencairan Dana
        </h3>

        <div data-testid="milestones-list" className="space-y-4">
          {milestones.map((milestone, i) => {
            const isReleased = milestone.status === 'released';

            return (
              <div
                key={milestone.id}
                data-testid={`milestone-${milestone.id}`}
                className={`rounded-2xl p-5 border transition-all ${
                  isReleased
                    ? 'dark:bg-[#1A1A24] bg-white dark:border-[#00C9B1]/20 border-[#00C9B1]/20'
                    : 'dark:bg-[#1A1A24] bg-white dark:border-[#2D3748] border-[#E2E8F0]'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                    isReleased ? 'bg-[#00C9B1]/15' : 'bg-[#F5A623]/10'
                  }`}>
                    {isReleased ? (
                      <CheckCircle2 size={22} className="text-[#00C9B1]" />
                    ) : (
                      <Lock size={22} className="text-[#F5A623]" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-1">
                      <div>
                        <p className={`font-bold text-base ${isReleased ? 'dark:text-white text-[#1A202C]' : 'dark:text-gray-300 text-[#2D3748]'}`}>
                          {milestone.label}
                        </p>
                        <p className="text-xs dark:text-gray-500 text-gray-400 mt-0.5">{milestone.description}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-black text-[#F5A623]">{formatPrice(milestone.amount)}</p>
                        <p className="text-xs dark:text-gray-500 text-gray-400">{milestone.percentage}%</p>
                      </div>
                    </div>

                    {/* Note */}
                    <div className={`flex items-center gap-2 text-xs mt-3 px-3 py-2 rounded-xl ${
                      isReleased
                        ? 'dark:bg-[#00C9B1]/10 bg-[#00C9B1]/5 text-[#00C9B1]'
                        : 'dark:bg-[#242430] bg-[#F7FAFC] dark:text-gray-500 text-gray-400'
                    }`}>
                      {isReleased ? (
                        <>
                          <CheckCircle2 size={12} />
                          <span>Dicairkan pada {milestone.releaseDate}</span>
                        </>
                      ) : (
                        <>
                          <Clock size={12} />
                          <span>{milestone.note} · {milestone.releaseDate}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Warning / Info footer */}
      <div className="mt-6 rounded-2xl p-4 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0] flex items-start gap-3">
        <AlertCircle size={16} className="text-[#F5A623] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold dark:text-gray-300 text-[#2D3748] mb-1">Kebijakan Refund</p>
          <p className="text-xs dark:text-gray-500 text-gray-400 leading-relaxed">
            Jika travel agent gagal memenuhi milestone, dana yang belum dicairkan akan dikembalikan ke rekening Anda dalam 3-5 hari kerja. 
            Hubungi tim support UMROHLOKA untuk bantuan.
          </p>
        </div>
      </div>
    </div>
  );
}
